import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/router';
import { useClearRefinements, useInstantSearch, useSearchBox } from 'react-instantsearch';

export const Context = React.createContext(null);

const defaultFilter = {
  selectedSearchTerm: undefined,
  fromDate: undefined,
  toDate: undefined,

  showSearchTerms: false,
  contentTypesOpen: false,
  isOpen: false
};

const Provider = ({ children }) => {
  const locale = useLocale();
  const router = useRouter();
  const [view, setView] = useState('stack');
  const { uiState, setUiState } = useInstantSearch();
  const { refine } = useClearRefinements();
  const { clear } = useSearchBox();

  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScrollPosition, setMaxScrollPosition] = useState(0);

  const [active, setActive] = useState({
    position: 0,
    index: 0
  });

  const [isTransitionActive, setIsTransitionActive] = useState(true);
  const [isFiltered, setIsFiltered] = useState();

  const [datePositions, setDatePositions] = useState([]);

  const updateScrollPosition = newPosition => {
    setScrollPosition(newPosition);

    const positions = [0, ...(datePositions || [])];
    const [position, index] = positions.reduce(
      (prev, curr, currentIndex) => {
        const currentDifference = Math.abs(curr - newPosition);
        const minDifference = Math.abs(prev[0] - newPosition);

        return currentDifference < minDifference ? [curr, currentIndex] : prev;
      },
      [positions[0], 0]
    );

    if (index !== active.index) setActive(prev => ({ ...prev, index }));
    if (scrollPosition !== position) setActive(prev => ({ ...prev, position }));
  };

  const [filter, setFilter] = useState(defaultFilter);

  useEffect(() => {
    const state = uiState[`futurium_archive_${locale}`];

    const filtered = Boolean(
      (filter.fromDate && filter.toDate) ||
        filter.selectedSearchTerm ||
        state.query ||
        state.refinementList ||
        state.range
    );

    setIsFiltered(filtered);
    if (filtered) window.scrollTo({ top: 0, behavior: 'instant' });
  }, [filter, uiState]);

  const getQueryParams = () => {
    return new URLSearchParams({
      archiveState: window.btoa(JSON.stringify({ uiState, view }))
    }).toString();
  };

  const setFilterToLastState = () => {
    if (!router.query.archiveState) return;

    try {
      const parsedState = JSON.parse(window.atob(router.query.archiveState));

      if (parsedState.uiState && typeof parsedState.uiState === 'object') {
        setUiState(parsedState.uiState);
      }

      if (['stack', 'list'].includes(parsedState.view)) {
        setView(parsedState.view);
      }
    } catch (error) {
      console.error('Something went wrong while parsing filter state');
      console.error(error);
    }
  };

  useEffect(() => {
    if (!isTransitionActive) setFilterToLastState();
  }, [isTransitionActive]);

  const setShowSearchTerms = i => setFilter(prev => ({ ...prev, showSearchTerms: i }));
  const setSelectedSearchTerm = i => setFilter(prev => ({ ...prev, selectedSearchTerm: i }));
  const setFromDate = i => setFilter(prev => ({ ...prev, fromDate: i }));
  const setToDate = i => setFilter(prev => ({ ...prev, toDate: i }));
  const setContentTypesOpen = i => setFilter(prev => ({ ...prev, contentTypesOpen: i }));
  const setIsModalOpen = i => setFilter(prev => ({ ...prev, isModalOpen: i }));

  const resetFilter = () => {
    setFilter(prev => ({
      ...prev,
      selectedSearchTerm: undefined,
      fromDate: undefined,
      toDate: undefined
    }));

    clear();
    setTimeout(() => refine(), 10);
  };

  return (
    <Context.Provider
      value={{
        filter,
        scrollPosition,
        setScrollPosition,
        maxScrollPosition,
        setMaxScrollPosition,
        setView,
        view,
        isTransitionActive,
        setIsTransitionActive,
        setIsModalOpen,
        setContentTypesOpen,
        setFromDate,
        setShowSearchTerms,
        setSelectedSearchTerm,
        setToDate,
        updateScrollPosition,
        isFiltered,
        setFilterToLastState,
        setIsFiltered,
        resetFilter,

        datePositions,
        setDatePositions,
        activeIndex: active.index,
        activePosition: active.position,
        getQueryParams
      }}
    >
      {children}
    </Context.Provider>
  );
};

Provider.propTypes = {
  children: PropTypes.element.isRequired
};

export default Provider;
